import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'

interface CarouselState {
	panel: HTMLElement
	slides: HTMLElement[]
	current: number
}

export class NumberedShowcase extends UcoastEl {
	static htmlSelector = 'numbered-showcase'

	private toggles: HTMLButtonElement[] = []
	private panels: HTMLElement[] = []
	private carousels: CarouselState[] = []
	private isTouch = false
	private autoplayMs = 0
	private autoplayTimer: number | undefined

	override connectedCallback() {
		super.connectedCallback()

		this.isTouch = window.matchMedia('(hover: none)').matches
		this.toggles = Array.from(q.ol<HTMLButtonElement>('[data-feature-toggle]', this) ?? [])
		this.panels = Array.from(q.ol<HTMLElement>('[data-feature-panel]', this) ?? [])
		this.autoplayMs = Number(this.dataset.carouselAutoplay) || 0

		this.toggles.forEach((toggle) => {
			const index = toggle.dataset.featureToggle
			if (!index) return
			toggle.addEventListener('click', () => this.activate(index))
			toggle.addEventListener('focus', () => this.activate(index))
			if (!this.isTouch) {
				toggle.addEventListener('mouseenter', () => this.activate(index))
			}
		})

		this.panels.forEach((panel) => this.initCarousel(panel))
		this.restartAutoplay()
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		if (this.autoplayTimer) window.clearInterval(this.autoplayTimer)
	}

	private initCarousel(panel: HTMLElement) {
		const carousel = q.os<HTMLElement>('[data-carousel]', panel)
		if (!carousel) return
		const slides = Array.from(q.ol<HTMLElement>('[data-slide]', carousel) ?? [])
		if (slides.length < 2) return

		const current = Math.max(
			0,
			slides.findIndex((slide) => slide.dataset.active === 'true'),
		)
		const state: CarouselState = { panel, slides, current }
		this.carousels.push(state)

		const prevBtn = q.os<HTMLButtonElement>('[data-carousel-prev]', panel)
		const nextBtn = q.os<HTMLButtonElement>('[data-carousel-next]', panel)

		prevBtn?.addEventListener('click', () => {
			this.showSlide(state, state.current - 1)
			this.restartAutoplay()
		})
		nextBtn?.addEventListener('click', () => {
			this.showSlide(state, state.current + 1)
			this.restartAutoplay()
		})
	}

	private showSlide(state: CarouselState, index: number) {
		const total = state.slides.length
		const next = ((index % total) + total) % total
		if (next === state.current) return

		state.slides[state.current]?.setAttribute('data-active', 'false')
		state.slides[next]?.setAttribute('data-active', 'true')
		state.current = next
	}

	private activate(index: string) {
		if (this.dataset.activeIndex === index) return
		this.dataset.activeIndex = index

		this.toggles.forEach((t) => {
			t.setAttribute('aria-pressed', String(t.dataset.featureToggle === index))
		})

		this.panels.forEach((p) => {
			const active = p.dataset.featurePanel === index
			p.dataset.active = String(active)
			p.setAttribute('aria-hidden', String(!active))
		})

		this.restartAutoplay()
	}

	private restartAutoplay() {
		if (this.autoplayTimer) {
			window.clearInterval(this.autoplayTimer)
			this.autoplayTimer = undefined
		}
		if (!this.autoplayMs || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

		this.autoplayTimer = window.setInterval(() => {
			const state = this.carousels.find((c) => c.panel.dataset.featurePanel === this.dataset.activeIndex)
			if (state) this.showSlide(state, state.current + 1)
		}, this.autoplayMs)
	}
}
